import React from 'react';
import { CreditCard, Clock, Users, Percent, Globe, DollarSign, Calendar } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета визового сбора
 */
const ResultsOverview = ({ results, formData, countries, visaTypes, exchangeRates }) => {
  if (!results) return null;

  // Получаем названия страны и типа визы
  const selectedCountry = countries.find(c => c.id === formData.country) || {};
  const selectedVisaType = visaTypes.find(t => t.id === formData.visaType) || {};

  // Определение типа въезда
  const entryTypeText = {
    single: 'Однократный',
    double: 'Двукратный',
    multiple: 'Многократный'
  }[formData.entryType] || 'Однократный';

  // Определение категории визы
  const visaCategoryText = {
    standard: 'Стандартная',
    premium: 'Премиум',
    economic: 'Эконом'
  }[formData.visaCategory] || 'Стандартная';

  // Определение времени обработки
  const processingTimeText = {
    standard: 'Стандартное (7-15 дней)',
    express: 'Ускоренное (3-5 дней)',
    urgent: 'Срочное (1-2 дня)',
    vip: 'VIP (в день подачи)'
  }[formData.processingTime] || 'Стандартное';

  return (
    <ToolResult title="Результаты расчета визового сбора">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Общая стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 p-5 border border-purple-100 dark:border-purple-800/50 shadow-sm">
          <h3 className="text-base font-medium text-purple-700 dark:text-purple-300 mb-1.5 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4"/>
            Общая стоимость визы
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-purple-800 dark:text-purple-200" aria-live="polite">
              {formatCurrency(results.totalRubCost)} ₽
            </span>
            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
              ({results.numApplicants} {getApplicantsText(results.numApplicants)})
            </span>
          </div>
          <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
            Включает все сборы и дополнительные услуги
          </p>
        </div>

        {/* Консульский сбор */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Globe className="w-4 h-4"/>
            Консульский сбор
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatCurrency(results.totalConsulateFee)} €
            </span>
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
              ({formatCurrency(results.consulateFee)} € × {results.numApplicants})
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Официальный сбор посольства/консульства
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Страна получения визы" 
          value={selectedCountry.name || formData.country} 
          color="purple" 
          icon={<Globe className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Тип визы" 
          value={`${selectedVisaType.name || formData.visaType}, ${entryTypeText.toLowerCase()}`} 
          color="blue" 
          icon={<CreditCard className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Срок действия визы" 
          value={`${formData.visaDuration} дней`} 
          icon={<Calendar className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Количество заявителей" 
          value={`${results.numApplicants} ${getApplicantsText(results.numApplicants)}`} 
          icon={<Users className="w-4 h-4"/>} 
        />

        <ResultItem 
          label="Время обработки" 
          value={processingTimeText} 
          icon={<Clock className="w-4 h-4"/>} 
        />
        
        {results.totalServiceFee > 0 && (
          <ResultItem 
            label="Сервисный сбор ВЦ" 
            value={`${formatCurrency(results.totalServiceFee)} € (${formatCurrency(results.serviceFee)} € × ${results.numApplicants})`} 
            color="amber" 
          />
        )}
        
        {results.totalInsuranceCost > 0 && (
          <ResultItem 
            label="Страховка" 
            value={`${formatCurrency(results.totalInsuranceCost)} ₽`} 
            color="emerald" 
          />
        )}
        
        {results.totalTranslationCost > 0 && (
          <ResultItem 
            label="Перевод документов" 
            value={`${formatCurrency(results.totalTranslationCost)} ₽`} 
            color="cyan" 
          />
        )}
        
        {results.totalDeliveryCost > 0 && (
          <ResultItem 
            label="Доставка документов" 
            value={`${formatCurrency(results.totalDeliveryCost)} ₽`} 
            color="orange" 
          />
        )}

        {/* Информация о курсах валют */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <Percent className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Использованные курсы валют</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                1 € = {formatNumber(exchangeRates.EUR)} ₽, 
                1 $ = {formatNumber(exchangeRates.USD)} ₽
                {exchangeRates.GBP && `, 1 £ = ${formatNumber(exchangeRates.GBP)} ₽`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Итог в других валютах */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Рубли</p>
          <p className="text-xl font-semibold text-gray-900 dark:text-white">
            {formatCurrency(results.totalRubCost)} ₽
          </p>
        </div>
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Евро</p>
          <p className="text-xl font-semibold text-gray-900 dark:text-white">
            {formatCurrency(results.totalRubCost / exchangeRates.EUR, 2)} €
          </p>
        </div>
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Доллары</p>
          <p className="text-xl font-semibold text-gray-900 dark:text-white">
            {formatCurrency(results.totalRubCost / exchangeRates.USD, 2)} $
          </p>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "заявитель"
const getApplicantsText = (count) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'заявителей';
  }
  
  if (lastDigit === 1) {
    return 'заявитель';
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'заявителя';
  }
  
  return 'заявителей';
};

export default ResultsOverview;